import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth, Roles } from "../providers/AuthContext";
import Chart from "react-apexcharts";
import {
    chartSeriesColors,
    chartGradientToColors,
    chartGradientDefaults,
} from "../theme/gitoColors";
import { getDateRange, getFriendlyDateRangeKey, getFriendlyDateRangeI18nKey } from "../helpers/DateTime";
import toast from "react-hot-toast";
import HeaderBar from "../components/HeaderBar";
import Sidebar from "../components/Sidebar";
import InputField from "../components/InputField";
import CustomDropdown from "../components/CustomDropdown";
import DatePicker from "../components/DatePicker";
import Dropdown from "../components/Dropdown";
import ChargingSessionSummary from "../components/session/ChargingSessionsSummary";
import ChargingDetailsModal from "../components/chargeMonitor/ChargingDetailsModal";
import LoadingSpinner from "../components/LoadingSpinner";
import FilterDimension from "../components/FilterDimension";
import SessionsTable from "../components/session/SessionsTable";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faChevronDown,
    faSearch,
    faPlus,
} from "@fortawesome/pro-regular-svg-icons";
import { subDays } from "date-fns";
import { getAllPartners } from "../api/Partners";
import {
    getAllChargingSitesForSessions,
} from "../api/ChargingSites";
import { getAllCustomersForSessions } from "../api/Customers";
import {
    getSessionStatisticsSummary,
    getSessionStatisticsChart,
    getSessionStatisticsList,
    exportSessionStatistics,
} from "../api/ChargingSession";

const FILTER_CUSTOMER = "Customer";
const FILTER_CHARGING_SITE = "Charging site";
const FILTER_PAYMENT_METHOD = "Payment method";
const FILTER_PARTNER = "Partner";

const createRawDimension = () => [
    {
        label: FILTER_CUSTOMER,
        options: [],
        selected: [],
        multiSelect: true,
        searchable: true,
        isActive: false,
    },
    {
        label: FILTER_CHARGING_SITE,
        options: [],
        selected: [],
        multiSelect: true,
        searchable: true,
        isActive: false,
    },
    {
        label: FILTER_PAYMENT_METHOD,
        options: [
            { value: "Invoice", label: "Invoice" },
            { value: "Card", label: "Card" },
            { value: "Other", label: "Other" },
        ],
        selected: [],
        multiSelect: true,
        searchable: true,
        isActive: false,
    },
    {
        label: FILTER_PARTNER,
        options: [],
        selected: [],
        multiSelect: true,
        searchable: true,
        isActive: false,
    },
];

function SessionsPage() {
    const { t } = useTranslation();
    const defaultEndDate = new Date();
    const defaultStartDate = subDays(defaultEndDate, 7);
    const { getAccessToken, hasRole } = useAuth();
    const [startDate, setStartDate] = useState(defaultStartDate);
    const [endDate, setEndDate] = useState(defaultEndDate);
    const [exporting, setExporting] = useState(false);
    const [dimensions, setDimensions] = useState([createRawDimension()]);
    const [customers, setCustomers] = useState([]);
    const [partners, setPartners] = useState([]);
    const [chargingSites, setChargingSites] = useState([]);
    const [summary, setSummary] = useState(null);
    const [summaryGroups, setSummaryGroups] = useState({});
    const [sessions, setSessions] = useState({});
    const [granularity, setGranularity] = useState("Days");
    const [aggregate, setAggregate] = useState("Sessions");
    const chartRef = useRef(null);
    const [search, setSearch] = useState("");
    const [rowsPerTable, setRowsPerTable] = useState(10);
    const [numberOfRows, setNumberOfRows] = useState(0);
    const [numberOfPages, setNumberOfPages] = useState(1);
    const [page, setPage] = useState(1);
    const [customersLoaded, setCustomersLoaded] = useState(false);
    const [sitesLoaded, setSitesLoaded] = useState(false);
    const [partnersLoaded, setPartnersLoaded] = useState(false);
    const [sessionsRefreshing, setSessionsRefreshing] = useState(true);
    const [selectedSession, setSelectedSession] = useState(null);
    const [isDetailsOpen, setIsDetailsOpen] = useState(false);
    const [chartData, setChartData] = useState({
        options: {
            chart: { width: "100%", height: "100px", id: "sessions-stats", toolbar: { show: false } },
            stroke: { curve: "smooth" },
            colors: chartSeriesColors.slice(0, 5),
            fill: {
                type: "gradient",
                gradient: {
                    ...chartGradientDefaults,
                    gradientToColors: chartGradientToColors.slice(0, 5),
                },
            },
            markers: { shape: "circle", size: 5 },
            dataLabels: { enabled: false },
            tooltip: { custom: function () { } },
            yaxis: { labels: { formatter: function (value) { return value && value.toFixed(0); } } },
            xaxis: { categories: [] },
            legend: { onItemClick: { toggleDataSeries: false }, onItemHover: { highlightDataSeries: true } },
        },
        series: [],
    });
    const isLoading = !customersLoaded || !sitesLoaded || !partnersLoaded;

    const pickList = (resp) => {
        if (Array.isArray(resp)) return resp;
        if (Array.isArray(resp?.items)) return resp.items;
        if (Array.isArray(resp?.data)) return resp.data;
        if (Array.isArray(resp?.customers)) return resp.customers;
        if (Array.isArray(resp?.chargingSites)) return resp.chargingSites;
        if (Array.isArray(resp?.partners)) return resp.partners;
        if (Array.isArray(resp?.operators)) return resp.operators;
        return [];
    };

    const toSafeDate = (value) => {
        if (!value || typeof value !== "string") return null;
        const date = new Date(value);
        return Number.isFinite(date.getTime()) ? date : null;
    };

    const normalizeSessionRow = (row) => {
        const start = toSafeDate(row?.startDateTime);
        const end = toSafeDate(row?.endDateTime);
        return {
            ...row,
            startDateTime: row?.startDateTime ?? null,
            endDateTime: row?.endDateTime ?? null,
            startDateTimeUtc: row?.startDateTimeUtc ?? (start ? start.toISOString() : null),
            endDateTimeUtc: row?.endDateTimeUtc ?? (end ? end.toISOString() : null),
        };
    };

    const getVisibleDimension = (dimension) => {
        if (hasRole(Roles.Customer)) {
            return dimension.filter((item) => item.label === FILTER_CHARGING_SITE);
        }
        if (!hasRole(Roles.Admin)) {
            return dimension.filter((item) =>
                [FILTER_CHARGING_SITE, FILTER_PAYMENT_METHOD, FILTER_CUSTOMER].includes(item.label)
            );
        }
        return dimension;
    };

    const mergeDimensionSet = (original, visible) =>
        original.map(
            (item) =>
                visible.find((candidate) => candidate.label === item.label) ?? item
        );

    const updateDimension = (visibleDimension, index) => {
        setDimensions((prev) =>
            prev.map((dimension, currentIndex) =>
                currentIndex === index
                    ? mergeDimensionSet(dimension, visibleDimension)
                    : dimension
            )
        );
    };

    const deleteDimension = (index) =>
        setDimensions((prev) =>
            prev.filter((_, currentIndex) => currentIndex !== index)
        );

    const addDimension = () => {
        setDimensions((prev) => [
            ...prev,
            (prev[0] ?? createRawDimension()).map((item) => ({
                ...item,
                selected: [],
                isActive: false,
            })),
        ]);
    };

    const handleDetails = (session) => {
        setSelectedSession(session);
        setIsDetailsOpen(true);
    };

    const handleFetchCustomers = async () => {
        try {
            const accessToken = await getAccessToken();
            const resp = await getAllCustomersForSessions(accessToken);
            const list = pickList(resp);
            setCustomers(list.sort((a, b) => (a?.name ?? "").localeCompare(b?.name ?? "")));
        } catch (error) {
            console.error("fetchCustomers failed:", error);
            toast.error(error?.message);
            setCustomers([]);
        } finally {
            setCustomersLoaded(true);
        }
    };

    const handleFetchChargingSites = async () => {
        try {
            const accessToken = await getAccessToken();
            const resp = await getAllChargingSitesForSessions(accessToken);
            const list = pickList(resp);
            setChargingSites(list.sort((a, b) => (a?.name ?? "").localeCompare(b?.name ?? "")));
        } catch (error) {
            console.error("fetchChargingSites failed:", error);
            toast.error(error?.message);
            setChargingSites([]);
        } finally {
            setSitesLoaded(true);
        }
    };

    const handleFetchPartners = async () => {
        try {
            const accessToken = await getAccessToken();
            const resp = await getAllPartners(accessToken);
            const list = pickList(resp);
            setPartners(list.sort((a, b) => (a?.name ?? "").localeCompare(b?.name ?? "")));
        } catch (error) {
            console.error("getAllPartners failed:", error);
            toast.error(error?.message);
            setPartners([]);
        } finally {
            setPartnersLoaded(true);
        }
    };

    useEffect(() => {
        handleFetchChargingSites();
        if (!hasRole(Roles.Customer)) {
            handleFetchCustomers();
        } else {
            setCustomersLoaded(true);
        }

        if (hasRole(Roles.Admin)) {
            handleFetchPartners();
        } else {
            setPartnersLoaded(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setDimensions((prev) =>
            prev.map((dimension) =>
                dimension.map((item) => {
                    if (item.label === FILTER_CUSTOMER) {
                        return {
                            ...item,
                            options: customers.map((customer) => customer.name),
                        };
                    }
                    if (item.label === FILTER_CHARGING_SITE) {
                        return {
                            ...item,
                            options: chargingSites.map((site) => site.name),
                        };
                    }
                    if (item.label === FILTER_PARTNER) {
                        return {
                            ...item,
                            options: partners.map((partner) => partner.name),
                        };
                    }
                    if (item.label === FILTER_PAYMENT_METHOD) {
                        return {
                            ...item,
                            options: [
                                { value: "Invoice", label: t("payment.Invoice") },
                                { value: "Card", label: t("payment.Card") },
                                { value: "Other", label: t("payment.Other") },
                            ],
                        };
                    }
                    return item;
                })
            )
        );
    }, [customers, chargingSites, partners, t]);

    const buildFilterFromDimension = async (selectedDimension) => {
        const startDateTime = new Date(
            startDate.getFullYear(),
            startDate.getMonth(),
            startDate.getDate(),
            0,
            0,
            0,
            0
        );
        const endDateTime = new Date(
            endDate.getFullYear(),
            endDate.getMonth(),
            endDate.getDate(),
            23,
            59,
            59,
            999
        );
        const customerSelected =
            selectedDimension.find((item) => item.label === FILTER_CUSTOMER)?.selected ?? [];
        const siteSelected =
            selectedDimension.find((item) => item.label === FILTER_CHARGING_SITE)?.selected ?? [];
        const paymentSelected =
            selectedDimension.find((item) => item.label === FILTER_PAYMENT_METHOD)?.selected ?? [];
        const partnerSelected =
            selectedDimension.find((item) => item.label === FILTER_PARTNER)?.selected ?? [];

        const customerPublicIds = customerSelected
            .map((name) => customers.find((customer) => customer.name === name)?.customerPublicId)
            .filter(Boolean);

        let chargingSitePublicIds = siteSelected
            .map((name) => chargingSites.find((site) => site.name === name)?.chargingSitePublicId)
            .filter(Boolean);

        const partnerPublicIds = partnerSelected
            .map((name) => partners.find((partner) => partner.name === name)?.partnerPublicId)
            .filter(Boolean);

        return {
            startDateTime: startDateTime.toISOString(),
            endDateTime: endDateTime.toISOString(),
            customerPublicIds,
            chargingSitePublicIds: Array.from(new Set(chargingSitePublicIds)),
            partnerPublicIds,
            operatorPublicIds: [],
            paymentMethodNames: paymentSelected
                .map((value) => {
                    if (value === "Invoice") return "Invoice";
                    if (value === "Card") return "Card";
                    if (value === "Other") return "Unknown";
                    return null;
                })
                .filter(Boolean),
        };
    };

    const fetchSummaryAndChart = async (accessToken) => {
        if (hasRole(Roles.CustomerService)) {
            setSummary(null);
            setSummaryGroups({});
            setChartData((prev) => ({
                ...prev,
                series: [],
                options: {
                    ...prev.options,
                    xaxis: {
                        ...prev.options.xaxis,
                        categories: [],
                    },
                },
            }));
            return;
        }

        const responses = await Promise.all(
            dimensions.map(async (selectedDimension, index) => {
                const filter = await buildFilterFromDimension(
                    selectedDimension,
                    accessToken
                );

                const [summaryResp, chartResp] = await Promise.all([
                    getSessionStatisticsSummary(accessToken, filter),
                    getSessionStatisticsChart(accessToken, {
                        ...filter,
                        granularity,
                        aggregate,
                    }),
                ]);

                return {
                    index,
                    summary: summaryResp ?? null,
                    chart: chartResp ?? null,
                };
            })
        );

        setSummaryGroups(
            Object.fromEntries(
                responses.map((response) => [response.index, response.summary])
            )
        );

        setSummary(dimensions.length === 1 ? responses[0]?.summary ?? null : null);

        setChartData((prev) => ({
            ...prev,
            series: responses.map((response, index) => ({
                name: t("sessions.series", { n: index + 1 }),
                data: Array.isArray(response.chart?.data) ? response.chart.data : [],
            })),
            options: {
                ...prev.options,
                xaxis: {
                    ...prev.options.xaxis,
                    categories: Array.isArray(responses[0]?.chart?.categories)
                        ? responses[0].chart.categories
                        : [],
                },
            },
        }));
    };

    const fetchTableSessions = async (accessToken) => {
        const tableEntries = await Promise.all(
            dimensions.map(async (selectedDimension, index) => {
                const filter = await buildFilterFromDimension(
                    selectedDimension,
                    accessToken
                );
                const resp = await getSessionStatisticsList(accessToken, {
                    ...filter,
                    search,
                    page: Number(page) || 1,
                    pageSize: Number(rowsPerTable) || 10,
                    sortColumn: "startDateTime",
                    sortOrder: "desc",
                });

                return [
                    index,
                    {
                        rows: (Array.isArray(resp?.sessions) ? resp.sessions : []).map(
                            normalizeSessionRow
                        ),
                        totalCount: Number(resp?.totalCount ?? 0),
                        totalPages: Number(resp?.totalPages ?? 1),
                    },
                ];
            })
        );

        const entriesByIndex = Object.fromEntries(tableEntries);
        const tableRows = Object.fromEntries(
            Object.entries(entriesByIndex).map(([key, value]) => [key, value.rows])
        );

        setSessions(tableRows);
        setNumberOfRows(
            Object.values(entriesByIndex).reduce(
                (max, value) => Math.max(max, value.totalCount),
                0
            )
        );
        setNumberOfPages(
            Object.values(entriesByIndex).reduce(
                (max, value) => Math.max(max, value.totalPages),
                1
            )
        );
    };

    const fetchAllStatistics = async () => {
        setSessionsRefreshing(true);
        try {
            const accessToken = await getAccessToken();
            await Promise.all([
                fetchSummaryAndChart(accessToken),
                fetchTableSessions(accessToken),
            ]);
        } catch (error) {
            console.error(error);
        } finally {
            setSessionsRefreshing(false);
        }
    };

    const selectionsKey = useMemo(
        () =>
            JSON.stringify(
                dimensions.map((dimension) =>
                    dimension.reduce((acc, item) => {
                        acc[item.label] = (item.selected ?? []).slice().sort();
                        return acc;
                    }, {})
                )
            ),
        [dimensions]
    );

    useEffect(() => {
        if (startDate && endDate && !isLoading) {
            setPage(1);
            fetchAllStatistics();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDate, endDate, isLoading, selectionsKey]);

    const handleRowsPerTableChange = (nextPageSize) => {
        const size = Number(nextPageSize);
        if (!Number.isFinite(size) || size <= 0) {
            return;
        }

        setPage(1);
        setRowsPerTable(size);
    };

    useEffect(() => {
        if (isLoading) {
            return;
        }

        if (search && search.length > 0 && search.length < 3) {
            return;
        }

        (async () => {
            try {
                const accessToken = await getAccessToken();
                await fetchTableSessions(accessToken);
            } catch (error) {
                console.error(error);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, rowsPerTable, search]);

    useEffect(() => {
        if (isLoading) {
            return;
        }

        (async () => {
            try {
                const accessToken = await getAccessToken();
                await fetchSummaryAndChart(accessToken);
            } catch (error) {
                console.error(error);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [granularity, aggregate]);

    useEffect(() => {
        setChartData((prev) => {
            const next = { ...prev, options: { ...prev.options } };
            next.options.tooltip = {
                custom: function ({ series, seriesIndex, dataPointIndex }) {
                    const yValue = series?.[seriesIndex]?.[dataPointIndex];
                    return `
            <div style="padding: 15px; background: white; border-radius: 8px; box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1); width: 170px; font-family: Inter, 'Segoe UI', system-ui, sans-serif;">
              <div style="font-size: 14px; color: #555;">
                ${aggregate === "Sessions" ? t("sessions.tooltipSessions", { value: Number.isFinite(yValue) ? Number(yValue).toFixed(0) : "" }) : t("sessions.tooltipKwh", { value: Number.isFinite(yValue) ? Number(yValue).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, " ") : "" })}
              </div>
            </div>
          `;
                },
            };
            return next;
        });
    }, [aggregate, t]);

    const getDaySelectionClass = () => {
        const rangeKey = getFriendlyDateRangeKey(startDate, endDate);
        if (["today", "last7days", "last30days", "thisYear"].includes(rangeKey)) {
            return "text-primary-500 font-bold";
        }
        return "text-mute-400";
    };

    const handleExportClick = async (e) => {
        e?.stopPropagation?.();
        if (exporting) return;
        setExporting(true);
        try {
            const accessToken = await getAccessToken();
            const filters = await Promise.all(
                dimensions.map((dimension) =>
                    buildFilterFromDimension(dimension, accessToken)
                )
            );

            const exportFilter = filters.reduce(
                (acc, filter) => ({
                    ...acc,
                    customerPublicIds: Array.from(
                        new Set([
                            ...(acc.customerPublicIds ?? []),
                            ...(filter.customerPublicIds ?? []),
                        ])
                    ),
                    chargingSitePublicIds: Array.from(
                        new Set([
                            ...(acc.chargingSitePublicIds ?? []),
                            ...(filter.chargingSitePublicIds ?? []),
                        ])
                    ),
                    partnerPublicIds: Array.from(
                        new Set([
                            ...(acc.partnerPublicIds ?? []),
                            ...(filter.partnerPublicIds ?? []),
                        ])
                    ),
                    paymentMethodNames: Array.from(
                        new Set([
                            ...(acc.paymentMethodNames ?? []),
                            ...(filter.paymentMethodNames ?? []),
                        ])
                    ),
                }),
                {
                    startDateTime: filters[0]?.startDateTime,
                    endDateTime: filters[0]?.endDateTime,
                    customerPublicIds: [],
                    chargingSitePublicIds: [],
                    partnerPublicIds: [],
                    paymentMethodNames: [],
                }
            );

            const blob = await exportSessionStatistics(accessToken, exportFilter);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = "sessions-export.xlsx";
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Export failed", error);
            toast.error(t("sessions.exportFailed"));
        } finally {
            setExporting(false);
        }
    };

    return (
        <>
            <HeaderBar />
            <div className="flex">
                <Sidebar />
                <div className="page">
                    {sessionsRefreshing && !isLoading && (
                        <div className="absolute inset-0 flex items-center justify-center z-[50] pointer-events-none">
                            <div className="bg-white shadow-xl rounded-2xl px-10 py-8 min-w-[300px] flex flex-col items-center gap-4 border border-mute-200">
                                <LoadingSpinner text={t("sessions.loading")} />
                            </div>
                        </div>
                    )}
                    {exporting && (
                        <div className="absolute inset-0 flex items-center justify-center z-[60]">
                            <div className="bg-white shadow-xl rounded-2xl px-10 py-8 min-w-[280px] flex flex-col items-center gap-4 border border-mute-200">
                                <LoadingSpinner text={t("sessions.exporting")} />
                            </div>
                        </div>
                    )}
                    {!isLoading && (
                        <div className="page-content">
                            <div className="flex flex-col md:flex-row justify-between md:items-center">
                                <h2 className="text-2xl text-black font-bold">{t("sessions.title")}</h2>
                                <div className="flex flex-wrap gap-2 mt-2 md:mt-0 min-w-0">
                                    <CustomDropdown
                                        isRight={true}
                                        ContentComponent={() => (
                                            <div className="w-40 flex flex-col gap-2 items-start bg-white p-4 rounded-lg">
                                                <button
                                                    className="text-sm hover:font-bold text-primary-500 hover:text-primary-600"
                                                    onClick={() => {
                                                        const today = new Date();
                                                        setStartDate(today);
                                                        setEndDate(today);
                                                    }}
                                                >
                                                    {t("dateRange.today")}
                                                </button>
                                                <button
                                                    className="text-sm hover:font-bold text-primary-500 hover:text-primary-600"
                                                    onClick={() => {
                                                        const lastWeek = new Date();
                                                        lastWeek.setDate(lastWeek.getDate() - 7);
                                                        setStartDate(lastWeek);
                                                        setEndDate(new Date());
                                                    }}
                                                >
                                                    {t("dateRange.last7Days")}
                                                </button>
                                                <button
                                                    className="text-sm hover:font-bold text-primary-500 hover:text-primary-600"
                                                    onClick={() => {
                                                        const lastMonth = new Date();
                                                        lastMonth.setMonth(lastMonth.getMonth() - 1);
                                                        setStartDate(lastMonth);
                                                        setEndDate(new Date());
                                                    }}
                                                >
                                                    {t("dateRange.last30Days")}
                                                </button>
                                                <button
                                                    className="text-sm hover:font-bold text-primary-500 hover:text-primary-600"
                                                    onClick={() => {
                                                        const startOfYear = new Date(
                                                            new Date().getFullYear(),
                                                            0,
                                                            1
                                                        );
                                                        setStartDate(startOfYear);
                                                        setEndDate(new Date());
                                                    }}
                                                >
                                                    {t("dateRange.thisYear")}
                                                </button>
                                            </div>
                                        )}
                                    >
                                        <div className="flex gap-2">
                                            <button
                                                disabled={hasRole(Roles.CustomerService)}
                                                className={`text-sm text-black ${getDaySelectionClass()}`}
                                            >
                                                {t(getFriendlyDateRangeI18nKey(startDate, endDate))}
                                            </button>
                                        </div>
                                    </CustomDropdown>
                                    <CustomDropdown
                                        isRight={true}
                                        ContentComponent={() => (
                                            <DatePicker
                                                startDate={startDate}
                                                endDate={endDate}
                                                setStartDate={setStartDate}
                                                setEndDate={setEndDate}
                                            />
                                        )}
                                    >
                                        <div className="flex gap-2">
                                            <button disabled={hasRole(Roles.CustomerService)} className="text-sm font-normal text-black">
                                                {getDateRange(startDate, endDate, t)} <FontAwesomeIcon icon={faChevronDown} />
                                            </button>
                                        </div>
                                    </CustomDropdown>
                                </div>
                            </div>
                            <div className="flex gap-4 flex-col-reverse md:flex-row">
                                <div className="flex flex-col gap-4 w-full">
                                    {dimensions.map((dimension, index) => (
                                        <FilterDimension
                                            key={`dimension-${index}`}
                                            dimension={getVisibleDimension(dimension)}
                                            setdimension={(nextDimension) => updateDimension(nextDimension, index)}
                                            index={index}
                                            allowDeleteDimension={dimensions.length > 1}
                                            deleteDimension={() => deleteDimension(index)}
                                        />
                                    ))}
                                </div>
                                {!hasRole(Roles.CustomerService) && (
                                    <div className="flex gap-2 items-start">
                                        <button
                                            className="btn-sm btn-outline-primary"
                                            disabled={dimensions.length >= 5}
                                            onClick={() => {
                                                if (dimensions.length < 5) {
                                                    addDimension();
                                                }
                                            }}
                                        >
                                            <FontAwesomeIcon icon={faPlus} /> {t("filter.addDimension")}
                                        </button>
                                        <button
                                            onMouseDown={(e) => e.stopPropagation()}
                                            className="btn-sm btn-outline-primary"
                                            disabled={!startDate || !endDate}
                                            onClick={handleExportClick}
                                        >
                                            {t("common.export")}
                                        </button>
                                    </div>
                                )}
                            </div>

                            {!hasRole(Roles.CustomerService) && (
                                <>
                                    <div className="flex gap-4 mt-4">
                                        <ChargingSessionSummary
                                            summary={dimensions.length === 1 ? summary : null}
                                            summaryGroups={summaryGroups}
                                            loading={sessionsRefreshing}
                                        />
                                    </div>
                                    <div className="surface-card w-full mt-[-2rem] overflow-hidden">
                                        <div className="rounded-2xl w-full">
                                            <div className="flex justify-between flex-col md:flex-row">
                                                <h3 className="ml-4 mt-4 text-xl text-mute-900 font-bold">{aggregate === "Sessions" ? t("sessions.chargingSessions") : t("sessions.energyUsed")}</h3>
                                                <div className="flex gap-2 px-4">
                                                    <div className="w-[8rem]">
                                                        <Dropdown options={[
                                                            { value: "Hours", label: t("sessions.granularity.Hours") },
                                                            { value: "Days", label: t("sessions.granularity.Days") },
                                                            { value: "Months", label: t("sessions.granularity.Months") },
                                                            { value: "Years", label: t("sessions.granularity.Years") },
                                                        ]} selectedOptions={[granularity]} onSelect={(value) => setGranularity(value)} />
                                                    </div>
                                                    <div className="w-[8rem]">
                                                        <Dropdown options={[
                                                            { value: "Sessions", label: t("sessions.aggregate.Sessions") },
                                                            { value: "kWh", label: t("sessions.aggregate.kWh") },
                                                            { value: "kWh (avg)", label: t("sessions.aggregate.kWh (avg)") },
                                                        ]} selectedOptions={[aggregate]} onSelect={(value) => setAggregate(value)} />
                                                    </div>
                                                </div>
                                            </div>
                                            <Chart options={chartData.options} series={chartData.series} type="area" height={"400px"} ref={chartRef} />
                                        </div>
                                    </div>
                                </>
                            )}

                            <div className="surface-card p-4 mt-6">
                                <div className="flex w-full justify-end">
                                    <div className="mb-2">
                                        <InputField
                                            icon={faSearch}
                                            placeholder={t("sessions.searchPlaceholder")}
                                            value={search}
                                            onChange={(v) => {
                                                const value = v.target.value;
                                                setSearch(value);
                                                if (value.length === 0 || value.length >= 3) setPage(1);
                                            }}
                                        />
                                    </div>
                                </div>
                                <SessionsTable
                                    dimensions={sessions}
                                    rowsPerTable={rowsPerTable}
                                    page={page}
                                    numberOfRows={numberOfRows}
                                    numberOfPages={numberOfPages}
                                    onRowsPerTableChange={handleRowsPerTableChange}
                                    setPage={setPage}
                                    onDetailsClick={handleDetails}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <ChargingDetailsModal
                isDetailsOpen={isDetailsOpen}
                setIsDetailsOpen={setIsDetailsOpen}
                selectedSessionId={selectedSession?.chargingSessionPublicId}
                selectedConnectorId={selectedSession?.chargingConnectorPublicId}
                selectedPointId={selectedSession?.chargingPointPublicId}
                startDateTimeUtc={selectedSession?.startDateTimeUtc}
                endDateTimeUtc={selectedSession?.endDateTimeUtc}
                canViewSessionDetails={selectedSession?.canViewSessionDetails !== false}
            />
        </>
    );
}

export default SessionsPage;
